import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { 
  Branch, 
  MenuItemMapping, 
  MenuMappingsQuery,
  Order, 
  BranchSummary,
  OrderSummary,
  BranchSyncStatusUpdate,
  BranchMenuSyncStatusUpdate,
  BranchPosConfig,
  SyncAllResult,
  SyncAllBranchesResult,
  ImportResult,
  BulkMappingSearchResult,
  BulkMappingUpdateRequest,
  BulkMappingUpdateResult,
  PaginatedResponse,
  DailyOrderReport,
  OrderDateRangeRequest,
  SalesSummaryReport,
  FinancialSettlementReport,
  AllDayCheckDto
} from '../types';
import { store } from '../store';
import { refreshAuthToken, forceLogout } from '../store/slices/authSlice';

// Flag to prevent multiple concurrent refresh attempts
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: any) => void;
}> = [];

class ApiService {
  private api: AxiosInstance;
  constructor() {
    this.api = axios.create({
      baseURL: process.env.REACT_APP_API_BASE_URL || 'http://localhost:5026/api',
      timeout: parseInt(process.env.REACT_APP_API_TIMEOUT || '30000'),
      headers: {
        'Content-Type': 'application/json',
      },
    });    // Request interceptor
    this.api.interceptors.request.use(
      (config) => {
        // Add auth token if available
        const token = localStorage.getItem('talabat_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        console.log(`[API] ${config.method?.toUpperCase()} ${config.url}`);
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );    // Response interceptor
    this.api.interceptors.response.use(
      (response: AxiosResponse) => {
        console.log(`[API] ${response.status} ${response.config.url}`);
        return response;
      },
      (error) => {
        console.error('[API Error]', {
          status: error.response?.status,
          url: error.config?.url,
          message: error.response?.data?.message || error.message,
        });        // Handle 401 Unauthorized responses
        if (error.response?.status === 401) {
          // Don't handle login or refresh requests to avoid infinite loops
          const isLoginRequest = error.config?.url?.includes('/auth/login');
          const isRefreshRequest = error.config?.url?.includes('/auth/refresh');
          
          if (!isLoginRequest && !isRefreshRequest) {
            const currentState = store.getState();
            const { refreshToken, isAuthenticated } = currentState.auth;
            
            if (isAuthenticated && refreshToken) {
              // If we're already refreshing, queue this request
              if (isRefreshing) {
                return new Promise((resolve, reject) => {
                  failedQueue.push({ resolve, reject });
                }).then(() => {
                  // Retry the request with the new token
                  const newToken = store.getState().auth.token;
                  if (newToken && error.config) {
                    error.config.headers.Authorization = `Bearer ${newToken}`;
                    return axios.request(error.config);
                  }
                  return Promise.reject(error);
                });
              }
              
              isRefreshing = true;
              
              // Attempt to refresh the token
              return store.dispatch(refreshAuthToken()).unwrap()
                .then(() => {
                  // Refresh succeeded - process queued requests
                  const newToken = store.getState().auth.token;
                  
                  // Retry the original request
                  if (newToken && error.config) {
                    error.config.headers.Authorization = `Bearer ${newToken}`;
                  }
                  
                  // Process all queued requests
                  failedQueue.forEach(({ resolve }) => resolve());
                  failedQueue = [];
                  isRefreshing = false;
                  
                  if (newToken && error.config) {
                    return axios.request(error.config);
                  }
                  return Promise.reject(error);
                })
                .catch((refreshError) => {
                  // Refresh failed - reject all queued requests and logout
                  console.error('Token refresh failed:', refreshError);
                  failedQueue.forEach(({ reject }) => reject(refreshError));
                  failedQueue = [];
                  isRefreshing = false;
                  
                  store.dispatch(forceLogout());
                  return Promise.reject(error);
                });
            } else {
              // No refresh token available, force logout
              store.dispatch(forceLogout());
            }
          }
        }
        
        return Promise.reject(error);
      }
    );
  }
  // Branch endpoints - Read and Update only (Create/Delete removed)
  async getBranches(): Promise<Branch[]> {
    const response = await this.api.get<Branch[]>('/restaurant-config/branches');
    return response.data;
  }

  async getBranchById(id: string): Promise<Branch> {
    const response = await this.api.get<Branch>(`/restaurant-config/branches/${id}`);
    return response.data;
  }

  async updateBranch(id: string, branch: Partial<Branch>): Promise<Branch> {
    const response = await this.api.put<Branch>(`/restaurant-config/branches/${id}`, branch);
    return response.data;
  }

  async updateBranchSyncStatus(id: string, status: BranchSyncStatusUpdate): Promise<Branch> {
    const response = await this.api.put<Branch>(`/restaurant-config/branches/${id}/sync-status`, status);
    return response.data;
  }

  async updateBranchMenuSyncStatus(id: string, status: BranchMenuSyncStatusUpdate): Promise<Branch> {
    const response = await this.api.put<Branch>(`/restaurant-config/branches/${id}/menu-sync-status`, status);
    return response.data;
  }
  async updateBranchPosConfig(id: string, config: BranchPosConfig): Promise<Branch> {
    const response = await this.api.put<Branch>(`/restaurant-config/branches/${id}/pos-config`, config);
    return response.data;
  }

  // Additional branch synchronization methods
  async syncBranchesWithTalabat(): Promise<SyncAllBranchesResult> {
    const response = await this.api.post<SyncAllBranchesResult>('/restaurant-config/branches/sync');
    return response.data;
  }

  async syncBranchMenuItems(id: string): Promise<{ message: string; syncedCount: number; menuItems: any[] }> {
    const response = await this.api.post<{ message: string; syncedCount: number; menuItems: any[] }>(`/restaurant-config/branches/${id}/menu-items/sync`);
    return response.data;
  }


  async syncAllBranchItems(id: string): Promise<SyncAllResult> {
    const response = await this.api.post<SyncAllResult>(`/restaurant-config/branches/${id}/sync-all`);
    return response.data;
  }
  // Menu mapping endpoints
  async getMenuMappings(query: MenuMappingsQuery): Promise<PaginatedResponse<MenuItemMapping>> {
    const response = await this.api.get<PaginatedResponse<MenuItemMapping>>('/mappings/paged', {
      params: {
        branchId: query.branchId || undefined,
        searchTerm: query.searchTerm || undefined,
        status: query.status || undefined,
        pageNumber: query.pageNumber,
        pageSize: query.pageSize,
      },
    });

    return response.data;
  }

  async createMenuMapping(mapping: Omit<MenuItemMapping, 'id' | 'lastUpdated' | 'createdAt'>): Promise<MenuItemMapping> {
    const response = await this.api.post<MenuItemMapping>('/mappings', mapping);
    return response.data;
  }

  async updateMenuMapping(id: number, mapping: Partial<MenuItemMapping>): Promise<MenuItemMapping> {
    const response = await this.api.put<MenuItemMapping>(`/mappings/${id}`, mapping);
    return response.data;
  }

  async deleteMenuMapping(id: number): Promise<void> {
    await this.api.delete(`/mappings/${id}`);
  }
  // Bulk operations for menu item mappings
  async searchMappingsByCloudName(searchTerm: string): Promise<BulkMappingSearchResult> {
    const response = await this.api.post<BulkMappingSearchResult>('/mappings/search-by-cloud-name', {
      cloudItemName: searchTerm
    });
    return response.data;
  }

  async bulkUpdateMappings(request: BulkMappingUpdateRequest): Promise<BulkMappingUpdateResult> {
    const response = await this.api.post<BulkMappingUpdateResult>('/mappings/bulk-update', request);
    return response.data;
  }

  // Order endpoints - Updated to support optional branchId parameter
  
  /**
   * Get new orders. If branchId is provided, returns orders for that branch only.
   * If branchId is not provided, returns orders for all branches.
   */
  async getNewOrders(branchId?: string): Promise<Order[]> {
    const params = branchId ? { branchId } : {};
    const response = await this.api.get<Order[]>('/orders/new', { params });
    return response.data;
  }


  /**
   * Get order history by date range. If branchId is provided, returns orders for that branch only.
   * If branchId is not provided, returns orders for all branches.
   */
  async getOrderHistory(branchId?: string | null, dateFrom?: string, dateTo?: string): Promise<Order[]> {
    const requestBody: Record<string, string | null> = {};
    if (branchId) requestBody.branchId = branchId;
    if (dateFrom) requestBody.dateFrom = dateFrom;
    if (dateTo) requestBody.dateTo = dateTo;
    
    const response = await this.api.post<Order[]>('/orders/date-range', requestBody);
    return response.data;
  }
  /**
   * Get order counters by status for dashboard. If branchId is provided, returns counters for that branch only.
   * If branchId is not provided, returns counters for all branches.
   */

  async getOrderCountersOnly(intervalDays: number = 1): Promise<Record<string, number>> {
    const response = await this.api.get<Record<string, number>>(`/orders/counters-only?intervalDays=${intervalDays}`);
    return response.data;
  }

  async getRevenueData(intervalDays: number = 1): Promise<any> {
    const response = await this.api.get(`/orders/revenue?intervalDays=${intervalDays}`);
    return response.data;
  }

  // Dashboard data methods
  async getBranchesSummary(): Promise<BranchSummary> {
    const branches = await this.getBranches();
    return {
      total: branches.length,
      active: branches.filter(b => b.isActive).length,
      syncEnabled: branches.filter(b => b.isSyncEnabled).length,
      menuSynced: branches.filter(b => b.isMenuSynced).length
    };
  }
  async getLatestOrders(limit: number = 10): Promise<OrderSummary[]> {
    const today = new Date();
    const y = today.getFullYear();
    const m = String(today.getMonth() + 1).padStart(2, '0');
    const d = String(today.getDate()).padStart(2, '0');
    const dayStr = `${y}-${m}-${d}`;

    // Send exact dates (YYYY-MM-DD) for both from/to to represent today's bucket
    const requestBody = {
      dateFrom: dayStr,
      dateTo: dayStr,
    };
    
    const response = await this.api.post<Order[]>('/orders/date-range', requestBody);

    // Get branches to map branch names
    const branches = await this.getBranches();
    const branchMap = branches.reduce((acc, branch) => {
      acc[branch.id] = branch.name;
      return acc;
    }, {} as Record<string, string>);    
    
    return response.data
      .slice(0, limit)
      .map(order => ({
        id: order.id,
        orderTime: order.orderTime,
        totalAmount: order.totalItems, // Using restaurant revenue instead of total order amount
        status: order.status,
        branchId: order.branchId,
        branchName: branchMap[order.branchId] || 'Unknown Branch',
        cloudOrderId: order.cloudOrderId,
        cloudShortCode: order.cloudShortCode,
        customerName: order.customerName,
        cloudOrderState: order.cloudOrderState
      }));
  }

  async getOrderById(orderId: string): Promise<Order> {
    const response = await this.api.get<Order>(`/orders/${orderId}`);
    return response.data;
  }

  async retryFailedOrder(orderId: string): Promise<{ message: string; order: Order }> {
    const response = await this.api.post<{ message: string; order: Order }>(`/orders/${orderId}/retry`);
    return response.data;
  }
  async importMappingsFromFile(branchId: string | undefined, file: File): Promise<ImportResult> {
    const formData = new FormData();
    formData.append('file', file);
    
    const url = branchId ? `/mappings/import/${branchId}` : '/mappings/import';
    const response = await this.api.post<ImportResult>(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }


  async exportMappings(branchId: string, format: 'csv' | 'excel'): Promise<Blob> {
    const response = await this.api.get(`/mappings/export/${branchId}?format=${format}`, {
      responseType: 'blob',
    });
    return response.data;
  }

  // Reports API Methods
  
  /**
   * Get daily order reports within a date range
   */
  async getDailyOrderReports(request: OrderDateRangeRequest): Promise<DailyOrderReport[]> {
    const response = await this.api.post<DailyOrderReport[]>('/reports/daily-orders', request);
    return response.data;
  }

  /**
   * Get sales summary report for a date range
   */
  async getSalesSummaryReport(request: OrderDateRangeRequest): Promise<SalesSummaryReport> {
    const response = await this.api.post<SalesSummaryReport>('/reports/sales-summary', request);
    return response.data;
  }

  /**
   * Get financial settlement report for a date range
   */
  async getFinancialSettlementReport(request: OrderDateRangeRequest): Promise<FinancialSettlementReport> {
    const response = await this.api.post<FinancialSettlementReport>('/reports/financial-settlement', request);
    return response.data;
  }

  /**
   * Get all day checks report
   */
  async getAllDayChecks(request: OrderDateRangeRequest): Promise<AllDayCheckDto[]> {
    const response = await this.api.post<AllDayCheckDto[]>('/reports/all-day-checks', request);
    return response.data;
  }

  // Monthly reports endpoint removed

}

export const apiService = new ApiService();
